import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ContactAddressType } from '../common/ContactAddress.type'
import { InputTextType } from '../common/InputType'
import userRole from '../config/userRole'
import { selectRole } from '../slices/authSlice'
import { CONTACT_FIELD_MAX_LENGTH } from '../util/contactFieldLimits'

export type ContactAddInfo = {
  address: {
    state: {
      address1: string
      address2: string
      city: string
      prefecture: string
      postalCode: string
      country: string
    }
    setState: {
      setAddress1: React.Dispatch<React.SetStateAction<string>>
      setAddress2: React.Dispatch<React.SetStateAction<string>>
      setCity: React.Dispatch<React.SetStateAction<string>>
      setPrefecture: React.Dispatch<React.SetStateAction<string>>
      setPostalCode: React.Dispatch<React.SetStateAction<string>>
      setCountry: React.Dispatch<React.SetStateAction<string>>
    }
  }
  other: {
    state: {
      organization: string
      position: string
      department: string
      mobilePhone: string
      telExtension: string
      altEmail: string
      fax: string
    }
    setState: {
      setOrganization: React.Dispatch<React.SetStateAction<string>>
      setPosition: React.Dispatch<React.SetStateAction<string>>
      setDepartment: React.Dispatch<React.SetStateAction<string>>
      setMobilePhone: React.Dispatch<React.SetStateAction<string>>
      setTelExtension: React.Dispatch<React.SetStateAction<string>>
      setAltEmail: React.Dispatch<React.SetStateAction<string>>
      setFax: React.Dispatch<React.SetStateAction<string>>
    }
    organizationInput: InputTextType
    positionInput: InputTextType
    departmentInput: InputTextType
    mobilePhoneInput: InputTextType
    telExtensionInput: InputTextType
    altEmailInput: InputTextType
    faxInput: InputTextType
  }
}

const useContactAddInfo = (
  contactAddress?: ContactAddressType,
): ContactAddInfo => {
  const role = useSelector(selectRole)

  // 共有連絡先（管理者が会社共有として登録する連絡先）は、内線番号が Asterisk 側で
  // 割り当てられるため入力不可とする。
  // ・追加画面: contactAddress が無いため、ログインユーザーがカンパニー管理者かどうかで判定
  //   （CreateContact が category を 'shared' にする条件と同一）
  // ・編集画面: 読み込んだ連絡先の category が 'shared' かどうかで判定
  const isSharedContact = contactAddress
    ? contactAddress.category === 'shared'
    : role === userRole['カンパニー管理者']

  const [organization, setOrganization] = useState(
    (contactAddress && contactAddress.organization ? contactAddress.organization : '')
  )
  const [position, setPosition] = useState(
    (contactAddress && contactAddress.position ? contactAddress.position : '')
  )
  const [department, setDepartment] = useState(
    (contactAddress && contactAddress.department ? contactAddress.department : '')
  )
  const [mobilePhone, setMobilePhone] = useState(
    (contactAddress && contactAddress.mobilePhone ? contactAddress.mobilePhone : '')
  )
  const [telExtension, setTelExtension] = useState(
    (contactAddress && contactAddress.telExtension ? contactAddress.telExtension : '')
  )
  const [altEmail, setAltEmail] = useState(
    (contactAddress && contactAddress.altEmail ? contactAddress.altEmail : '')
  )
  const [fax, setFax] = useState(
    (contactAddress && contactAddress.fax ? contactAddress.fax : '')
  )
  const [address1, setAddress1] = useState(
    (contactAddress && contactAddress.address1 ? contactAddress.address1 : '')
  )
  const [address2, setAddress2] = useState(
    (contactAddress && contactAddress.address2 ? contactAddress.address2 : '')
  )
  const [city, setCity] = useState(
    (contactAddress && contactAddress.city ? contactAddress.city : '')
  )
  const [prefecture, setPrefecture] = useState(
    (contactAddress && contactAddress.prefecture ? contactAddress.prefecture : '')
  )
  const [postalCode, setPostalCode] = useState(
    (contactAddress && contactAddress.postalCode ? contactAddress.postalCode : '')
  )
  const [country, setCountry] = useState(
    (contactAddress && contactAddress.country ? contactAddress.country : '')
  )

  useEffect(() => {
    if (contactAddress?.organization) {
      setOrganization(contactAddress?.organization)
    }
  }, [contactAddress?.organization])

  useEffect(() => {
    if (contactAddress?.position) {
      setPosition(contactAddress?.position)
    }
  }, [contactAddress?.position])

  useEffect(() => {
    if (contactAddress?.department) {
      setDepartment(contactAddress?.department)
    }
  }, [contactAddress?.department])

  useEffect(() => {
    if (contactAddress?.mobilePhone) {
      setMobilePhone(contactAddress?.mobilePhone)
    }
  }, [contactAddress?.mobilePhone])

  useEffect(() => {
    if (contactAddress?.telExtension) {
      setTelExtension(contactAddress?.telExtension)
    }
  }, [contactAddress?.telExtension])

  useEffect(() => {
    if (contactAddress?.altEmail) {
      setAltEmail(contactAddress?.altEmail)
    }
  }, [contactAddress?.altEmail])

  useEffect(() => {
    if (contactAddress?.fax) {
      setFax(contactAddress?.fax)
    }
  }, [contactAddress?.fax])

  useEffect(() => {
    if (contactAddress?.address1) {
      setAddress1(contactAddress?.address1)
    }
  }, [contactAddress?.address1])

  useEffect(() => {
    if (contactAddress?.address2) {
      setAddress2(contactAddress?.address2)
    }
  }, [contactAddress?.address2])

  useEffect(() => {
    if (contactAddress?.city) {
      setCity(contactAddress?.city)
    }
  }, [contactAddress?.city])

  useEffect(() => {
    if (contactAddress?.prefecture) {
      setPrefecture(contactAddress?.prefecture)
    }
  }, [contactAddress?.prefecture])

  useEffect(() => {
    if (contactAddress?.postalCode) {
      setPostalCode(contactAddress?.postalCode)
    }
  }, [contactAddress?.postalCode])

  useEffect(() => {
    if (contactAddress?.country) {
      setCountry(contactAddress?.country)
    }
  }, [contactAddress?.country])

  const organizationInput: InputTextType = {
    type: 'text',
    id: 'organization',
    label: '組織',
    placeholder: '',
    required: false,
    autoComplete: 'organization',
    state: organization,
    setState: setOrganization,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.organization,
  }

  const departmentInput: InputTextType = {
    type: 'text',
    id: 'department',
    label: '部署',
    placeholder: '',
    required: false,
    autoComplete: 'none',
    state: department,
    setState: setDepartment,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.department,
  }

  const positionInput: InputTextType = {
    type: 'text',
    id: 'position',
    label: '役職',
    placeholder: '',
    required: false,
    autoComplete: 'organization-title',
    state: position,
    setState: setPosition,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.position,
  }

  const mobilePhoneInput: InputTextType = {
    type: 'tel',
    id: 'mobilePhone',
    label: '携帯電話',
    placeholder: '（ハイフンなし）',
    required: false,
    autoComplete: 'none',
    state: mobilePhone,
    setState: setMobilePhone,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.mobilePhone,
  }

  const telExtensionInput: InputTextType = {
    type: 'tel',
    id: 'telExtension',
    label: '内線番号',
    placeholder: '',
    required: false,
    autoComplete: 'none',
    state: telExtension,
    setState: setTelExtension,
    // 共有連絡先（管理者登録）の場合は内線番号を入力不可。個人連絡先（一般ユーザー）は入力可。
    readonly: isSharedContact,
    maxLength: CONTACT_FIELD_MAX_LENGTH.telExtension,
  }

  const altEmailInput: InputTextType = {
    type: 'email',
    id: 'altEmail',
    label: 'その他Eメール',
    placeholder: '',
    required: false,
    autoComplete: 'none',
    state: altEmail,
    setState: setAltEmail,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.altEmail,
  }

  const faxInput: InputTextType = {
    type: 'tel',
    id: 'fax',
    label: 'Fax',
    placeholder: '',
    required: false,
    autoComplete: 'none',
    state: fax,
    setState: setFax,
    readonly: false,
    maxLength: CONTACT_FIELD_MAX_LENGTH.fax,
  }

  return {
    address: {
      state: {
        address1,
        address2,
        city,
        prefecture,
        postalCode,
        country,
      },
      setState: {
        setAddress1,
        setAddress2,
        setCity,
        setPrefecture,
        setPostalCode,
        setCountry,
      },
    },
    other: {
      state: {
        organization,
        position,
        department,
        mobilePhone,
        telExtension,
        altEmail,
        fax,
      },
      setState: {
        setOrganization,
        setPosition,
        setDepartment,
        setMobilePhone,
        setTelExtension,
        setAltEmail,
        setFax,
      },
      organizationInput,
      positionInput,
      departmentInput,
      mobilePhoneInput,
      telExtensionInput,
      altEmailInput,
      faxInput,
    },
  }
}

export default useContactAddInfo
